import { useQuery } from "@tanstack/react-query";
import { format, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronDown, PlaneLanding, PlaneTakeoff, TicketMinus, Timer } from "lucide-react";

import type { UsersTicketsHistorySchema } from "@upclaim/sdk/src/data-contracts";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatInTimezone } from "@/features/claims/lib/dates";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import adminAPI from "@/services/api";

import { CouponHistoryDetail, InvoluntaryBadge, TicketStatusBadge } from "./coupon-history-detail";
import { TicketHistorySkeleton } from "./ticket-history-skeleton";

type UserTicketHistory = UsersTicketsHistorySchema[number];
type Ticket = UserTicketHistory["tickets"][number];
type LegHistory = Ticket["legs"][number];

function formatFlightDate(date: Date, tz: string): string {
  return formatInTimezone(new Date(date), tz, "HH:mm");
}

/** Airline logo/initials avatar with an EC261 status dot — port of the fwb-avatar block of EnrichedTicket.vue. */
function AirlineAvatar({ airline }: { airline: LegHistory[number]["flight"]["airline"] }) {
  const avatar = (
    <Avatar className="relative">
      {airline?.legalData?.iconURL ? (
        <AvatarImage src={airline.legalData.iconURL} alt={airline.name} />
      ) : null}
      <AvatarFallback>{airline?.iata}</AvatarFallback>
      <span
        className={cn(
          "absolute right-0 bottom-0 size-2 rounded-full ring-2 ring-white",
          airline?.isEC261 ? "bg-green-500" : "bg-gray-400",
        )}
        aria-hidden="true"
      />
    </Avatar>
  );

  if (airline?.isEC261) {
    return avatar;
  }

  return (
    <Tooltip>
      <TooltipTrigger>{avatar}</TooltipTrigger>
      <TooltipContent>Compagnie non européenne</TooltipContent>
    </Tooltip>
  );
}

/** Port of EnrichedTicket.vue — leg display + per-coupon history detail (CouponHistoryDetail.vue). */
function EnrichedTicket({ ticket, impactedFlightId }: { ticket: Ticket; impactedFlightId: string | null }) {
  if (!ticket?.legs?.length) {
    return null;
  }

  const lastLeg = [...ticket.legs].reverse()[0];
  const ticketSyncDate = format(new Date(ticket.syncedAt || (lastLeg[0].syncedAt as Date)), "dd MMMM", {
    locale: fr,
  });
  const currentDate = new Date();

  return (
    <div className="mb-6 text-left text-sm font-semibold leading-6 text-gray-900">
      <div className="flex items-center justify-between">
        <div className="mb-2">E-Ticket · {ticket.ticketNumber}</div>
        <div className="text-xs font-normal italic text-gray-500">Synchronisé le {ticketSyncDate}</div>
      </div>

      {ticket.legs.map((legHistory: LegHistory, index: number) => {
        const leg = legHistory[0];
        const flight = leg.flight;
        const isFuture =
          isValid(new Date(flight.scheduledArrivalUTC)) &&
          new Date(flight.scheduledArrivalUTC).getTime() > currentDate.getTime();
        const isImpacted = legHistory.some((l) => l.flightId === impactedFlightId);
        const isDisrupted = flight.monitoringStatus === "CANCELLED" || flight.monitoringStatus === "DELAYED";
        const actualDepartureLocal = flight.actualDepartureUTC
          ? formatFlightDate(flight.actualDepartureUTC as Date, flight.departureAirport.timezone)
          : null;
        const scheduledArrivalLocal =
          leg.localArrivalString?.split(" ")[1] ||
          formatFlightDate(flight.scheduledArrivalUTC, flight.arrivalAirport.timezone);
        const actualArrivalLocal = flight.inGateArrivalUTC
          ? formatFlightDate(flight.inGateArrivalUTC as Date, flight.arrivalAirport.timezone)
          : null;

        return (
          <Collapsible
            // biome-ignore lint/suspicious/noArrayIndexKey: legs have no stable id
            key={index}
            className={cn(
              "relative mb-2 block rounded-lg bg-white px-4 py-5 shadow-sm",
              isFuture ? "border border-dashed" : "border border-gray-300",
              isImpacted && "relative mx-1 my-5 rounded-lg border-2 border-yellow-500",
            )}
          >
            <CollapsibleTrigger className="group flex w-full items-start gap-2 text-left">
              <div className="text-left flex-1">
                <div className="mx-auto flex max-w-4xl justify-between gap-x-4">
                  <div className="flex min-w-0 gap-x-4">
                    <AirlineAvatar airline={flight.airline} />
                    <div className="min-w-0 flex-auto">
                      <p className="text-sm font-semibold leading-6 text-gray-900">
                        Vol{" "}
                        <a
                          href={`https://fr.flightaware.com/live/flight/${flight.flightIdentifier}/history`}
                          target="_blank"
                          rel="noreferrer"
                          className="link font-semibold"
                        >
                          {flight.flightIdentifier}
                        </a>{" "}
                        opéré par {flight.airline?.name}
                      </p>
                      <div className="mt-1 flex-row gap-10 text-sm leading-5 text-gray-600">
                        <div className="relative inline-flex items-center">
                          <p className="inline-flex items-center">
                            <PlaneTakeoff size={14} className="mr-1" />
                            Départ : {leg.localDepartureString?.split(" ")[1]}
                          </p>
                          {isDisrupted ? (
                            <p className="inline-flex items-center text-sm font-semibold leading-7">
                              <span className="mx-1.5 font-bold text-gray-800">→</span>
                              {flight.monitoringStatus === "CANCELLED" ? (
                                <span className="text-gray-900">vol annulé</span>
                              ) : actualDepartureLocal ? (
                                <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                                  <Timer className="h-4 w-4" color="orange" />
                                  Réel : {actualDepartureLocal}
                                </span>
                              ) : null}
                            </p>
                          ) : null}
                        </div>
                        <div className="relative mt-0.5">
                          <p className="inline-flex items-center">
                            <PlaneLanding size={14} className="mr-1" />
                            Arrivée : {scheduledArrivalLocal}
                            {isDisrupted ? (
                              <span className="inline-flex items-center text-sm font-semibold leading-7">
                                <span className="mx-1.5 font-bold text-gray-800">→</span>
                                {flight.monitoringStatus === "CANCELLED" ? (
                                  <span className="text-gray-900">vol annulé</span>
                                ) : actualArrivalLocal ? (
                                  <span className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
                                    <Timer className="h-4 w-4" color="orange" />
                                    Réel : {actualArrivalLocal}
                                  </span>
                                ) : null}
                              </span>
                            ) : null}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-x-4">
                    <div className="hidden sm:flex sm:flex-col sm:items-end">
                      <p className="text-xs font-normal leading-6 text-gray-600">
                        {leg.localDepartureString?.split(" ")[0]}
                      </p>
                      <p className="text-sm font-bold leading-6 text-gray-900">
                        {flight.departureAirport.iata} → {flight.arrivalAirport.iata}
                      </p>
                      <InvoluntaryBadge involuntaryIndicator={leg.involuntaryIndicator} />
                      <p className="mt-1 text-xs leading-5 text-gray-500">
                        <TicketStatusBadge status={leg.status} statusDescription={leg.statusDescription} />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <ChevronDown
                className="mt-1 h-5 w-5 flex-none text-gray-400 transition-transform group-data-[panel-open]:rotate-180"
                aria-hidden="true"
              />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-4 pb-2 text-sm text-gray-500">
                <CouponHistoryDetail legHistory={legHistory} />
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}

/** Port of DisplayTicketHistory.vue — e-tickets history per passenger. */
export function TicketHistory({
  claimId,
  impactedFlightId,
}: {
  claimId: string;
  impactedFlightId: string | null;
}) {
  const { data: usersTickets, isPending } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "etickets-history"],
    queryFn: () => adminAPI.retrieveClaimETicketsHistory(claimId),
  });

  if (isPending) {
    return <TicketHistorySkeleton />;
  }

  if (!usersTickets?.length) {
    return (
      <div className="mb-8 rounded-md border px-8 py-4">
        <div className="text-center">
          <TicketMinus size={32} className="mx-auto text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">Aucun billet détecté</h3>
          <p className="mt-1 text-sm text-gray-500">
            Cela peut arriver pour des dossiers hors GDS, NDC, ou avec des PNRs archivés.
          </p>
        </div>
      </div>
    );
  }

  // One tab per passenger: the endpoint can return the same user several times — duplicate tab
  // values make Base UI's highlighted-tab tracking ping-pong into "Maximum update depth".
  // Merge the entries (tickets deduped by id) instead of rendering duplicate triggers.
  const ticketsByUser = new Map<string, (typeof usersTickets)[number]>();
  for (const entry of usersTickets) {
    const existing = ticketsByUser.get(entry.user.id);
    if (existing) {
      const knownTicketIds = new Set(existing.tickets.map((ticket) => ticket.id));
      existing.tickets.push(...entry.tickets.filter((ticket) => !knownTicketIds.has(ticket.id)));
    } else {
      ticketsByUser.set(entry.user.id, { ...entry, tickets: [...entry.tickets] });
    }
  }
  const userTabs = [...ticketsByUser.values()];

  return (
    <Tabs defaultValue={userTabs[0].user.id} className="w-full py-5">
      <TabsList>
        {userTabs.map(({ user }) => (
          <TabsTrigger key={user.id} value={user.id}>
            {user.firstName}
          </TabsTrigger>
        ))}
      </TabsList>
      {userTabs.map(({ user, tickets }) => (
        <TabsContent key={user.id} value={user.id}>
          {tickets.map((ticket) => (
            <EnrichedTicket key={ticket.id} ticket={ticket} impactedFlightId={impactedFlightId} />
          ))}
        </TabsContent>
      ))}
    </Tabs>
  );
}
